import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { openModal } from '../../store/slices/uiSlice';

// Helper function to get default redirect URL based on user type
const getDefaultRedirectForUserType = (userType) => {
  switch (userType) {
    case 'Admin':
    case 'SubAdmin':
      return '/admin';
    case 'Airliner':
      return '/airliner/profile';
    case 'JetSetter':
      return '/jetsetter/profile/user-details';
    default:
      return '/search-flight';
  }
};

const ProtectedRoute = ({ children, requiredUserTypes = [], redirectTo = '/' }) => {
  const { isAuthenticated, user, isInitialized } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const location = useLocation();

  // Debug: Log authentication state
  // console.log('🔐 ProtectedRoute Check:', {
  //   path: location.pathname,
  //   isAuthenticated,
  //   isInitialized,
  //   user: user?.email,
  //   userType: user?.user_type,
  //   requiredUserTypes,
  //   hasToken: !!localStorage.getItem('token'),
  //   hasUser: !!localStorage.getItem('user')
  // });

  // Wait for auth state to be initialized
  if (!isInitialized) {
    // console.log('⏳ Auth not initialized yet, waiting...');
    return <div>Loading...</div>;
  }

  // If not authenticated, open login modal and redirect to home
  if (!isAuthenticated) {
    // console.log('❌ Not authenticated, redirecting to home');
    
    // Store the attempted URL to redirect back after login
    sessionStorage.setItem('redirectAfterLogin', location.pathname);
    
    // Open login modal instead of redirecting
    setTimeout(() => {
      dispatch(openModal('login'));
    }, 100);
    
    // Redirect to home page
    return <Navigate to={redirectTo} replace />;
  }

  // If user type restrictions are specified, check user type
  if (requiredUserTypes.length > 0 && user) {
    if (!requiredUserTypes.includes(user.user_type)) {
      // console.log('❌ User type not allowed:', user.user_type, 'Required:', requiredUserTypes);
      
      // Redirect to user's default page based on their role
      const defaultRedirect = getDefaultRedirectForUserType(user.user_type);
      return <Navigate to={defaultRedirect} replace />;
    }
  }

  // console.log('✅ Access granted');
  return children;
};

// Specific protected route components for different user types
export const JetSetterRoute = ({ children }) => (
  <ProtectedRoute requiredUserTypes={['JetSetter']} redirectTo="/">
    {children}
  </ProtectedRoute>
);

export const AdminRoute = ({ children }) => (
  <ProtectedRoute requiredUserTypes={['Admin', 'SubAdmin']} redirectTo="/">
    {children}
  </ProtectedRoute>
);

export const AirlinerRoute = ({ children }) => (
  <ProtectedRoute requiredUserTypes={['Airliner']} redirectTo="/">
    {children}
  </ProtectedRoute>
);

// General authenticated route (any logged-in user)
export const AuthenticatedRoute = ({ children }) => (
  <ProtectedRoute redirectTo="/">
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;